import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import 'dayjs/locale/de';
import 'dayjs/locale/hi';
import 'dayjs/locale/ru';
import 'dayjs/locale/tr';
import 'dayjs/locale/en';

const DATE_LOCALE = LOCALE.toLowerCase().replace('_', '-').split('-')[0];

dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);
dayjs.locale(DATE_LOCALE);

type PeriodTuple = [Date, Date];
type NullablePeriodTuple = [Date | null, Date | null];

/**
 * Набор утилит для работы с датами и периодами на dayjs.
 */
export const DateHelpers = {
    /**
     * Возвращает Dayjs объект для текущего момента времени.
     */
    today: (): Dayjs => dayjs(),

    /**
     * Возвращает текущую дату с обнулённым временем (00:00:00).
     */
    getToday: (): Date => dayjs().startOf('day').toDate(),

    /**
     * Возвращает минимальную дату — 01.01.2020 (с обнулённым временем).
     */
    getMinDate: (): Date => dayjs('2020-01-01').startOf('day').toDate(),

    /**
     * Возвращает вчерашнюю дату (с обнулённым временем).
     */
    getYesterday: (): Date => dayjs().subtract(1, 'day').startOf('day').toDate(),

    /**
     * Преобразует переданное значение к объекту Date через dayjs.
     * @param date - строка, число, Date или Dayjs
     */
    toDate: (date: string | number | Date | Dayjs): Date => dayjs(date).toDate(),

    /**
     * Возвращает первый день предыдущего месяца (с обнулённым временем).
     */
    getPreviousMonth: (): Date => dayjs().subtract(1, 'month').startOf('month').toDate(),

    /**
     * Возвращает первый день текущего месяца (с обнулённым временем).
     */
    getCurrentMonth: (): Date => dayjs().startOf('month').toDate(),

    /**
     * Парсит строку периода вида 'DD.MM.YYYY - DD.MM.YYYY' в массив из двух дат.
     * Если дата невалидна или не полная — вернёт null вместо неё.
     * @param value - строка с периодом
     * @param format - формат дат (по умолчанию 'DD.MM.YYYY')
     */
    parsePeriodString: (value: string, format: string = 'DD.MM.YYYY'): NullablePeriodTuple => {
        if (!value) return [null, null];
        const [start, end] = value.split('-').map((part) => part.trim());
        const startDate = start && start.length === 10 ? dayjs(start, format, true) : null;
        const endDate = end && end.length === 10 ? dayjs(end, format, true) : null;
        return [
            startDate && startDate.isValid() ? startDate.toDate() : null,
            endDate && endDate.isValid() ? endDate.toDate() : null,
        ];
    },

    /**
     * Форматирует дату в строку.
     * @param params.date - дата
     * @param params.format - формат (по умолчанию 'DD.MM.YYYY')
     */
    dateFormatter: ({
        date,
        format = 'DD.MM.YYYY',
    }: {
        date: Date | string;
        format?: string;
    }): string => {
        if (!date) return '';

        if (typeof date === 'string') {
            const parsedDMY = dayjs(date, 'DD.MM.YYYY', true).locale(DATE_LOCALE);
            if (parsedDMY.isValid()) return parsedDMY.format(format);

            const parsedISO = dayjs(date).locale(DATE_LOCALE);
            return parsedISO.isValid() ? parsedISO.format(format) : '';
        }

        return dayjs(date).locale(DATE_LOCALE).format(format);
    },

    /**
     * Форматирует ISO-дату/дату-время в строку.
     * Подходит для значений вида '2026-04-03T00:00:00+03:00'.
     * Если строка не является валидной ISO-датой — вернёт пустую строку.
     * @param params.date - ISO-строка даты/даты-времени
     * @param params.format - формат dayjs (по умолчанию 'DD.MM.YYYY')
     */
    formatIsoDate: ({ date, format = 'DD.MM.YYYY' }: { date: string; format?: string }): string => {
        if (!date) return '';

        const parsed = dayjs(date).locale(DATE_LOCALE);
        return parsed.isValid() ? parsed.format(format) : '';
    },

    /**
     * Гарантирует, что openToDate не выйдет за границы minDate/maxDate.
     * Если date вне диапазона — вернёт minDate или maxDate.
     * Если date не задана — вернёт fallback.
     */
    getSafeOpenToDate: (
        date: Date | null | undefined,
        minDate: Date,
        maxDate: Date,
        fallback: Date
    ): Date => {
        if (date) {
            if (dayjs(date).isBefore(dayjs(minDate), 'month')) return minDate;
            if (dayjs(date).isAfter(dayjs(maxDate), 'month')) return maxDate;
            return date;
        }
        return fallback;
    },

    /**
     * Возвращает понедельник недели, к которой принадлежит указанная дата.
     * @param date - любая дата (по умолчанию сегодня)
     */
    getMondayOfWeek: (date: Date = new Date()): Date => dayjs(date).startOf('isoWeek').toDate(),

    /**
     * Возвращает воскресенье недели, к которой принадлежит указанная дата.
     * @param date - любая дата (по умолчанию сегодня)
     */
    getSundayOfWeek: (date: Date = new Date()): Date => dayjs(date).endOf('isoWeek').toDate(),

    /**
     * Возвращает первый день месяца для переданной даты.
     * @param date - любая дата (по умолчанию сегодня)
     */
    getFirstDayOfMonth: (date: Date = new Date()): Date => dayjs(date).startOf('month').toDate(),

    /**
     * Возвращает последний день месяца для переданной даты.
     * @param date - любая дата (по умолчанию сегодня)
     */
    getLastDayOfMonth: (date: Date = new Date()): Date => dayjs(date).endOf('month').toDate(),

    /**
     * Возвращает первый день предыдущего месяца.
     */
    getFirstDayOfPreviousMonth: (): Date => dayjs().subtract(1, 'month').startOf('month').toDate(),

    /**
     * Возвращает последний день предыдущего месяца.
     */
    getLastDayOfPreviousMonth: (): Date => dayjs().subtract(1, 'month').endOf('month').toDate(),

    /**
     * Возвращает первый день квартала для переданной даты.
     * @param date - любая дата (по умолчанию сегодня)
     */
    getFirstDayOfQuarter: (date: Date = new Date()): Date =>
        dayjs(date).startOf('quarter').toDate(),

    /**
     * Возвращает диапазон "прошлая неделя": понедельник-воскресенье прошлой недели.
     */
    getPreviousWeekPeriod: (): PeriodTuple => [
        DateHelpers.getMondayOfWeek(dayjs().subtract(1, 'week').toDate()),
        DateHelpers.getSundayOfWeek(dayjs().subtract(1, 'week').toDate()),
    ],
    getPreviousSevenDays: (): PeriodTuple => [
        dayjs().subtract(6, 'day').toDate(),
        DateHelpers.getToday(),
    ],

    /**
     * Возвращает диапазон "эта неделя": понедельник-текущий день.
     */
    getThisWeekPeriod: (): PeriodTuple => [DateHelpers.getMondayOfWeek(), DateHelpers.getToday()],

    /**
     * Возвращает диапазон "этот месяц": первый день месяца-текущий день.
     */
    getThisMonthPeriod: (): PeriodTuple => [
        DateHelpers.getFirstDayOfMonth(),
        DateHelpers.getToday(),
    ],

    /**
     * Возвращает диапазон "прошлый месяц": первый-последний день прошлого месяца.
     */
    getPreviousMonthPeriod: (): PeriodTuple => [
        DateHelpers.getFirstDayOfPreviousMonth(),
        DateHelpers.getLastDayOfPreviousMonth(),
    ],

    /**
     * Возвращает диапазон "текущий квартал": первый день квартала-текущий день.
     */
    getCurrentQuarterPeriod: (): PeriodTuple => [
        DateHelpers.getFirstDayOfQuarter(),
        DateHelpers.getToday(),
    ],
    /**
     * Проверяет, где находится дата по отношению к minDate и maxDate.
     * @param dateStr - строка с датой (например, '06.08.2025')
     * @param minDate - минимально допустимая дата
     * @param maxDate - максимально допустимая дата
     * @param format - формат строки (по умолчанию 'DD.MM.YYYY')
     * @returns 'before' | 'after' | 'inRange' | 'invalid'
     */
    getDateRangeStatus: (
        dateStr: string,
        minDate: Date,
        maxDate: Date,
        format: string = 'DD.MM.YYYY'
    ): 'before' | 'after' | 'inRange' | 'invalid' => {
        const date = dayjs(dateStr, format, true);
        if (!date.isValid()) return 'invalid';

        if (date.isBefore(dayjs(minDate), 'day')) return 'before';
        if (date.isAfter(dayjs(maxDate), 'day')) return 'after';
        return 'inRange';
    },
    /**
     * Возвращает текущий год.
     * @param date - любая дата
     */
    getDateYear: (date: Date) => dayjs(date).year(),

    /**
     * Проверяет, является ли дата валидной.
     * @param date - любая дата (число, строка, null, undefined)
     * @returns true, если дата валидная, иначе false
     * @example
     * isDateValid('2025-08-06'); // true
     * isDateValid('06.08.2025'); // true
     * isDateValid(null); // false
     * isDateValid(undefined); // false
     * isDateValid(123456); // false
     */
    isDateValid: (date: Date | string | number | null | undefined): boolean => {
        if (!date) return false;
        if (typeof date === 'number') return false;
        return dayjs(date, ['YYYY-MM-DD', 'DD.MM.YYYY'], true).isValid();
    },
};
